// client/src/pages/Game/Battlefield/hooks/useCombatAnimations.js
//
// Détection des événements de jeu par diff d'état → animations de combat.
//
// Stratégie : à chaque mise à jour de gameState, comparer avec l'état
// précédent (champs, PV, pendingAttack, lastAction) et déclencher :
//   - résolution de combat → choc (clash) + retours + morts
//   - morts hors combat (sorts, pièges, létal) → destruction
//   - dégâts / soins sur cartes survivantes → flash + nombre flottant
//   - dégâts sur les PV → impact sur le cercle de vie
//   - SPELL_CAST → éclat élémentaire ; TRAP_SET → reflet violet
//
// Les positions des cartes sont snapshotées UN cycle en retard pour
// connaître la dernière position DOM d'une carte disparue du render.

import { useEffect, useRef } from 'react';
import {
  playCombatClash,
  playDirectAttack,
  playCardDeath,
  playSpellCast,
  playTrapSet,
  playDamageFlash,
  playHealFlash,
  playLifeHit,
  playEffectBolt,
  playEffectHit,
  playStatFlash,
  playLifeGain,
  playCardShowcase,
  floatText,
  flyCloneToCard,
  hideRealCard,
  restoreRealCard,
  sweepStuckCards,
  cloneCardAt,
  shockwave,
  ELEMENT_COLORS,
} from '../utils/combatAnimations';
import { setLifeDisplay, clearLifeDisplay } from '../utils/lifeDisplay';

const keyOf = (c) => c.instanceId || c.id;

// getBoundingClientRect tolérant (null si démonté)
const rectOfLike = (el) => {
  try {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 ? r : null;
  } catch (e) { return null; }
};

export default function useCombatAnimations(gameState, getCardElement) {
  // R34v : balaye les cartes restées invisibles (animation coincée)
  useEffect(() => {
    const iv = setInterval(sweepStuckCards, 1000);
    return () => clearInterval(iv);
  }, []);

  const initRef = useRef(false);
  const prevRef = useRef(null);
  const rectSnapRef = useRef(new Map());

  // Snapshot des rects de toutes les cartes de zone (monstres + combat)
  const refreshRects = () => {
    const map = new Map();
    document.querySelectorAll(
      '.monster-card[data-instance-id], .battle-monster-card[data-instance-id]'
    ).forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        map.set(el.dataset.instanceId, r);
      }
    });
    rectSnapRef.current = map;
  };

  useEffect(() => {
    if (!gameState) return;

    const snapshotState = (gs) => ({
      player: (gs.playerField || []).map((c) => ({ ...c })),
      opp: (gs.opponentField || []).map((c) => ({ ...c })),
      playerLife: gs.playerLife,
      opponentLife: gs.opponentLife,
      pendingAttack: gs.pendingAttack,
      lastAction: gs.lastAction,
    });

    // Premier rendu : initialiser sans animer
    if (!initRef.current) {
      initRef.current = true;
      prevRef.current = snapshotState(gameState);
      refreshRects();
      return;
    }

    const prev = prevRef.current;
    clearLifeDisplay('player');
    clearLifeDisplay('opponent');
    const newPlayer = gameState.playerField || [];
    const newOpp = gameState.opponentField || [];

    const prevAll = [
      ...prev.player.map((c) => ({ ...c, side: 'player' })),
      ...prev.opp.map((c) => ({ ...c, side: 'opponent' })),
    ];
    const newMap = new Map([
      ...newPlayer.map((c) => [keyOf(c), c]),
      ...newOpp.map((c) => [keyOf(c), c]),
    ]);
    const prevMap = new Map(prevAll.map((c) => [keyOf(c), c]));

    // ── 1. Résolution de combat ────────────────────────────────
    // Declaration d'attaque/defense : la carte quitte la zone monstre pour la
    // zone de combat -> vol fluide au lieu d'une teleportation seche.
    prevAll.forEach((c) => {
      if (c.zone !== 'monster') return;
      const now = newMap.get(keyOf(c));
      if (!now || now.zone !== 'battle') return;
      const rect0 = rectSnapRef.current.get(keyOf(c));
      const targetEl0 = document.querySelector('[data-instance-id="' + keyOf(c) + '"]');
      if (rect0 && targetEl0) {
        flyCloneToCard({ card: now, rect: rect0, targetEl: targetEl0, getCardElement });
      }
    });

    const la = gameState.lastAction;
    const prevLa = prev.lastAction;
    const isNewAction = !!(la && la !== prevLa);
    // Une annulation de déclaration (UNDECLARE_*) fait aussi disparaître
    // pendingAttack quand on retire le DERNIER attaquant — ce n'est PAS
    // une résolution de combat : pas de choc, juste un retour en zone
    // monstres (géré en 1b).
    const isUndeclare = !!(la && (la.type === 'UNDECLARE_ATTACK' || la.type === 'UNDECLARE_DEFEND'));

    const prevHadPendingAttack = !!(
      prev.pendingAttack && prev.pendingAttack.attackers
      && prev.pendingAttack.attackers.length > 0
    );
    const nowHasPendingAttack = !!(
      gameState.pendingAttack && gameState.pendingAttack.attackers
      && gameState.pendingAttack.attackers.length > 0
    );

    const wasInBattle = prevAll.filter((c) => c.zone === 'battle');
    const lifeDropped = {
      player: (gameState.playerLife ?? prev.playerLife) < prev.playerLife,
      opponent: (gameState.opponentLife ?? prev.opponentLife) < prev.opponentLife,
    };
    const lifeDelta = {
      player: prev.playerLife - (gameState.playerLife ?? prev.playerLife),
      opponent: prev.opponentLife - (gameState.opponentLife ?? prev.opponentLife),
    };

    let resolutionHandled = false;

    if (!isUndeclare && prevHadPendingAttack && !nowHasPendingAttack && wasInBattle.length > 0) {
      resolutionHandled = true;

      const participants = wasInBattle
        .map((c) => ({
          card: c,
          rect: rectSnapRef.current.get(keyOf(c)),
          role: c.role,
          side: c.side,
        }))
        .filter((p) => p.rect);

      const survivors = participants.filter((p) => newMap.has(keyOf(p.card)));
      const dead = participants.filter((p) => !newMap.has(keyOf(p.card)));
      const defenders = participants.filter((p) => p.role !== 'attacker');
      const attackers = participants.filter((p) => p.role === 'attacker');

      // Côté défenseur = côté opposé à l'attaquant
      const defenderSide = attackers.length
        ? (attackers[0].side === 'player' ? 'opponent' : 'player')
        : null;

      if (participants.length > 0) {
        if (defenders.length === 0 && defenderSide && lifeDropped[defenderSide]) {
          // ── Attaque directe : les attaquants foncent sur les PV ──
          const lifeEl = document.querySelector(`.${defenderSide}-life`);
          const atkList = participants.filter((p) => p.role === 'attacker');

          // Un nombre de dégâts PAR attaquant (ATK locale, PIERCING double) ;
          // le dernier absorbe l'écart avec le total serveur (lifeDelta)
          // pour rester exact même si un piège a modifié les stats en vol.
          const pierceMul = (card) => ((card.abilities || []).includes('PIERCING') ? 2 : 1);
          const estimates = atkList.map((p) => Math.max(0, (p.card.attack || 0) * pierceMul(p.card)));
          const sumEst = estimates.reduce((s, v) => s + v, 0);
          const total = Math.max(0, lifeDelta[defenderSide] || 0);
          if (atkList.length > 0) {
            estimates[atkList.length - 1] = Math.max(0,
              estimates[atkList.length - 1] + (total - sumEst));
          }

          // Morts + vols de retour joués UNE SEULE fois, au DERNIER impact.
          // (Avant : chaque onLanded relançait flyCloneToCard sur tous les
          // survivants — deux vols qui se chevauchent capturent
          // prevOpacity='0' et laissaient les vraies cartes invisibles.)
          // R34v : les PV affichés partent des PV d'avant combat et
          // descendent attaque par attaque (état serveur déjà final).
          const prevLife = defenderSide === 'player' ? prev.playerLife : prev.opponentLife;
          setLifeDisplay(defenderSide, prevLife);
          let shownLife = prevLife;

          const runAftermath = () => {
            dead.forEach((d) => playCardDeath({
              card: d.card,
              rect: d.rect,
              getCardElement,
              graveyardEl: document.querySelector('.' + d.side + '-deck .deck-card'),
            }));
            // Les attaquants survivants gerent eux-memes leur vol de retour
            // (playDirectAttack : dash vers les PV puis arc vers leur carte).
          };

          atkList.forEach((p, i) => {
            const isLast = i === atkList.length - 1;
            const atkTarget = document.querySelector(
              '[data-instance-id="' + keyOf(p.card) + '"]'
            );
            setTimeout(() => {
              playDirectAttack({
                card: p.card,
                rect: p.rect,
                lifeEl,
                targetEl: atkTarget,
                getCardElement,
                amount: estimates[i],
                onLanded: () => {
                  shownLife = Math.max(0, shownLife - estimates[i]);
                  setLifeDisplay(defenderSide, shownLife);
                  if (isLast) {
                    runAftermath();
                    // Rendre la main à l'état réel une fois le dernier
                    // impact joué (les deux valeurs coïncident).
                    setTimeout(() => clearLifeDisplay(defenderSide), 900);
                  }
                },
              });
            }, i * 560);
          });
        } else {
          // ── R34x : résolution SÉQUENTIELLE, un attaquant à la fois ──
          // Chaque attaquant (de gauche à droite) joue SA propre animation :
          // bloqué → choc en tête-à-tête avec SON défenseur (defendingAgainst),
          // non bloqué → dash sur le cercle de PV du joueur défenseur.
          const atkList = attackers.slice().sort((a, b) => a.rect.x - b.rect.x);
          const lifeEl = defenderSide ? document.querySelector('.' + defenderSide + '-life') : null;

          // Appariement attaquant ↔ défenseur (le défenseur porte
          // defendingAgainst = position de l'attaquant qu'il bloque).
          const defByPos = new Map();
          defenders.forEach((d) => {
            const k = String((d.card && d.card.defendingAgainst) ?? '');
            if (k && !defByPos.has(k)) defByPos.set(k, d);
          });
          atkList.forEach((a) => {
            a.defender = defByPos.get(String((a.card && a.card.position) ?? '')) || null;
          });

          // PV progressifs pour les attaquants non bloqués (même logique que
          // l'attaque directe : état serveur déjà final, override d'affichage).
          const pierceMul = (card) => ((card.abilities || []).includes('PIERCING') ? 2 : 1);
          const unblocked = atkList.filter((a) => !a.defender);
          const est = unblocked.map((a) => Math.max(0, (a.card.attack || 0) * pierceMul(a.card)));
          const sumEst = est.reduce((x, v) => x + v, 0);
          const total = defenderSide ? Math.max(0, lifeDelta[defenderSide] || 0) : 0;
          if (unblocked.length > 0) {
            est[unblocked.length - 1] = Math.max(0, est[unblocked.length - 1] + (total - sumEst));
          }
          let unblockedDone = 0;
          let lifeOverrideInit = false;
          let shownLife = defenderSide === 'player' ? prev.playerLife : prev.opponentLife;

          // ── R34z : clones « parqués » en zone d'attaque ──
          // À la résolution serveur, React replace aussitôt les vraies cartes
          // en zone monstres. On les masque et on parque un clone de chaque
          // participant à SA position de bataille : la zone d'attaque reste
          // peuplée pendant toute la séquence. Chaque attaquant anime SON
          // clone (choc 1v1 ou dash sur les PV puis retour au parc). À la fin
          // uniquement, tous les survivants regagnent la zone monstres.
          const park = new Map(); // keyOf(card) -> { p, el }
          const realEls = [];
          const centerOf = (r) => ({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
          const parkOne = (pp) => {
            const el = cloneCardAt(pp.card, pp.rect, getCardElement);
            park.set(keyOf(pp.card), { p: pp, el });
          };
          const unPark = (pp) => {
            const e = park.get(keyOf(pp.card));
            if (e) { e.el.remove(); park.delete(keyOf(pp.card)); }
          };
          participants.forEach((pp) => {
            const real = document.querySelector('[data-instance-id="' + keyOf(pp.card) + '"]');
            if (real) { hideRealCard(real); realEls.push(real); }
            parkOne(pp);
          });
          // Filet de sécurité : jamais de carte invisible à vie
          const safety = setTimeout(() => {
            realEls.forEach((el) => restoreRealCard(el, { flash: false }));
            park.forEach(({ el }) => el.remove());
            park.clear();
          }, 15000);

          const graveElOf = (side) =>
            document.querySelector('.' + side + '-deck .deck-card');

          // Fin de séquence : retours GROUPÉS vers la zone monstres
          const finishAll = () => {
            clearTimeout(safety);
            const otherSide = defenderSide === 'player' ? 'opponent' : 'player';
            if (lifeDropped[otherSide]) {
              playLifeHit(document.querySelector('.' + otherSide + '-life'), lifeDelta[otherSide]);
            }
            park.forEach(({ p: pp, el }) => {
              el.remove();
              const real = document.querySelector('[data-instance-id="' + keyOf(pp.card) + '"]');
              if (real) {
                flyCloneToCard({
                  card: pp.card,
                  rect: pp.rect,
                  targetEl: real,
                  getCardElement,
                  owned: true,
                });
              }
            });
            park.clear();
            setTimeout(() => {
              realEls.forEach((el) => restoreRealCard(el, { flash: false }));
            }, 2300);
          };

          const step = (i) => {
            if (i >= atkList.length) { finishAll(); return; }
            const a = atkList[i];

            if (a.defender) {
              // Choc 1 contre 1 : playCombatClash re-crée ses propres clones
              // aux mêmes positions → on retire les clones parqués de la paire.
              unPark(a);
              unPark(a.defender);
              playCombatClash({
                participants: [a, a.defender],
                getCardElement,
                onResolved: () => {
                  [a, a.defender].forEach((pp) => {
                    if (dead.includes(pp)) {
                      playCardDeath({
                        card: pp.card,
                        rect: pp.rect,
                        getCardElement,
                        graveyardEl: graveElOf(pp.side),
                      });
                    } else {
                      // Survivant : re-parqué en zone d'attaque jusqu'à la fin
                      parkOne(pp);
                    }
                  });
                  setTimeout(() => step(i + 1), 280);
                },
              });
            } else {
              // Non bloqué : le clone PARQUÉ dash sur les PV puis revient
              const dmg = est[unblockedDone] || 0;
              unblockedDone += 1;
              const entry = park.get(keyOf(a.card));
              const finishStep = () => {
                if (dead.includes(a)) {
                  unPark(a);
                  playCardDeath({
                    card: a.card,
                    rect: a.rect,
                    getCardElement,
                    graveyardEl: graveElOf(a.side),
                  });
                }
                setTimeout(() => step(i + 1), 220);
              };
              if (dmg > 0 && lifeEl && entry) {
                if (!lifeOverrideInit) {
                  lifeOverrideInit = true;
                  setLifeDisplay(
                    defenderSide,
                    defenderSide === 'player' ? prev.playerLife : prev.opponentLife
                  );
                }
                const el = entry.el;
                const c = centerOf(a.rect);
                const lc = centerOf(lifeEl.getBoundingClientRect());
                const dx = lc.x - c.x;
                const dy = lc.y - c.y;
                const rot = dx > 0 ? 10 : -10;
                // anticipation + dash
                el.animate([
                  { transform: 'translate(0,0) scale(1)', filter: 'brightness(1)' },
                  {
                    transform: 'translate(' + (-dx * 0.08) + 'px,' + (-dy * 0.08) + 'px) scale(1.08)',
                    filter: 'brightness(1.5) drop-shadow(0 0 12px #ffd166)',
                    offset: 0.3,
                    easing: 'ease-out',
                  },
                  {
                    transform: 'translate(' + dx + 'px,' + dy + 'px) scale(0.88) rotate(' + rot + 'deg)',
                    filter: 'brightness(1.8) drop-shadow(0 0 14px #ff5252)',
                    easing: 'cubic-bezier(0.55, 0, 0.9, 0.4)',
                  },
                ], { duration: 440, fill: 'forwards' }).finished
                  .then(() => {
                    shockwave(lc.x, lc.y, { color: '#ff5252', size: 150, duration: 500 });
                    playLifeHit(lifeEl, dmg);
                    shownLife = Math.max(0, shownLife - dmg);
                    setLifeDisplay(defenderSide, shownLife);
                    if (unblockedDone === unblocked.length) {
                      setTimeout(() => clearLifeDisplay(defenderSide), 900);
                    }
                    // retour au parc (zone d'attaque)
                    el.animate([
                      {
                        transform: 'translate(' + dx + 'px,' + dy + 'px) scale(0.88) rotate(' + rot + 'deg)',
                        filter: 'brightness(1.8)',
                      },
                      { transform: 'translate(0,0) scale(1)', filter: 'brightness(1)' },
                    ], { duration: 560, easing: 'cubic-bezier(0.3, 0.7, 0.3, 1)' })
                      .finished.then(finishStep).catch(finishStep);
                  })
                  .catch(finishStep);
              } else {
                finishStep();
              }
            }
          };
          step(0);
        }
      }
    }

    // ── 1b. Annulation de déclaration ─────────────────────────
    // La carte retirée de la zone de combat reglide vers la zone des
    // monstres (aucun choc, aucun dégât — juste le vol de retour).
    if (isUndeclare) {
      prevAll.forEach((c) => {
        if (c.zone !== 'battle') return;
        const now = newMap.get(keyOf(c));
        if (!now || now.zone === 'battle') return; // encore déclarée
        const rect = rectSnapRef.current.get(keyOf(c));
        const targetEl = document.querySelector(
          `[data-instance-id="${keyOf(c)}"]`
        );
        if (rect && targetEl) {
          flyCloneToCard({
            card: now,
            rect,
            targetEl,
            getCardElement,
          });
        }
      });
    }

    // ── R34hh : showcase « effet déclenché » ───────────────────
    // La carte qui déclenche un effet (sort, capacité, invocation à effet,
    // piège) s'affiche en grand avec un reflet AVANT les animations de
    // dégâts — le joueur comprend la CAUSE de ce qui se passe.
    let effectDelay = 0;
    let effectColor = ELEMENT_COLORS.neutral;
    if (!resolutionHandled && isNewAction && la.card && la.card.name && la.player) {
      const t = la.type;
      const wantShowcase =
        t === 'SPELL_CAST' || t === 'ABILITY_ACTIVATED' || t === 'TRAP_ACTIVATED'
        || (t === 'SUMMON' && (la.card.effectCount || 0) > 0);
      if (wantShowcase) {
        effectColor = ELEMENT_COLORS[(la.card.element || '').toLowerCase()]
          || ELEMENT_COLORS.neutral;
        const label = t === 'SPELL_CAST' ? 'Sort lancé'
          : t === 'ABILITY_ACTIVATED' ? 'Capacité activée'
          : t === 'TRAP_ACTIVATED' ? 'Piège activé'
          : 'Effet d\'invocation';
        playCardShowcase({ card: la.card, label, getCardElement });
        effectDelay = 950;
      }
    }

    // ── 2. Morts hors combat (sorts, létal, pièges) ────────────
    if (!resolutionHandled) {
      prevAll.forEach((c) => {
        if (c.zone === 'battle') return; // déjà géré par la résolution
        if (newMap.has(keyOf(c))) return;
        const rect = rectSnapRef.current.get(keyOf(c));
        if (!rect) return;
        const doDeath = () => playCardDeath({
          card: c,
          rect,
          getCardElement,
          graveyardEl: document.querySelector(`.${c.side}-deck .deck-card`),
        });
        if (effectDelay > 0) {
          // R34hh : projectile depuis la carte montrée vers la victime,
          // impact PUIS mort — on comprend qui a tué quoi.
          const cx = rect.left + rect.width / 2;
          const cy = rect.top + rect.height / 2;
          const anchor = document.createElement('div');
          anchor.style.cssText = 'position:fixed;left:' + cx + 'px;top:' + cy + 'px;width:1px;height:1px;pointer-events:none;';
          document.body.appendChild(anchor);
          const bf = document.querySelector('.battlefield-frame');
          const br = bf ? bf.getBoundingClientRect() : { left: cx, top: cy, width: 0, height: 0 };
          setTimeout(() => {
            playEffectBolt({
              from: { x: br.left + br.width / 2, y: br.top + br.height * 0.42 },
              toEl: anchor,
              color: effectColor,
              onImpact: () => {
                floatText('K.O.', cx, rect.top - 6, { color: '#ff5252', size: 24 });
                setTimeout(() => anchor.remove(), 60);
              },
            });
            setTimeout(doDeath, 340);
          }, effectDelay);
        } else {
          doDeath();
        }
      });
    }

    // ── 3. Dégâts / soins sur cartes survivantes ───────────────
    // R34dd : dégâts hors combat = coups d'EFFET (invocation ON_SUMMON,
    // sort, piège) → projectile élémentaire depuis la source + impact
    // visible (onde de choc, recul, nombre). Pendant une résolution de
    // combat, tout est déjà joué par la séquence §1 : on n'anime rien.
    if (!resolutionHandled) {
      const effectDamages = [];
      const statChanges = [];
      [...newPlayer, ...newOpp].forEach((c) => {
        const old = prevMap.get(keyOf(c));
        if (!old) return;
        const el = document.querySelector(`[data-instance-id="${keyOf(c)}"]`);
        if (!el) return;
        const hpNow = c.currentHP ?? c.defense;
        const hpOld = old.currentHP ?? old.defense;
        if (hpNow < hpOld) {
          effectDamages.push({ el, amount: hpOld - hpNow });
        } else if (hpNow > hpOld) {
          playHealFlash(el, hpNow - hpOld);
        }
        // R34ee : buffs/debuffs de stats (BOOST/REDUCE/EXCHANGE/TRANSFORM)
        const dAtk = (c.attack ?? 0) - (old.attack ?? 0);
        const dDef = (c.defense ?? 0) - (old.defense ?? 0);
        if (dAtk !== 0 || dDef !== 0) {
          statChanges.push({ el, atk: dAtk, def: dDef });
        }
      });

      if (effectDamages.length > 0) {
        // Source du coup : la carte invoquée (effet ON_SUMMON) ou le
        // lanceur du sort (tiers du terrain), sinon le centre du plateau.
        const laEff = la || {};
        let src = null;
        const color = ELEMENT_COLORS[(laEff.card && laEff.card.element || '').toLowerCase()]
          || ELEMENT_COLORS.neutral;
        if ((laEff.type === 'SUMMON' || laEff.type === 'ABILITY_ACTIVATED' || laEff.type === 'TRAP_ACTIVATED') && laEff.instanceId) {
          const srcEl = document.querySelector(
            `[data-instance-id="${laEff.instanceId}"]`
          );
          if (srcEl) {
            const sr = rectOfLike(srcEl);
            if (sr) src = { x: sr.left + sr.width / 2, y: sr.top + sr.height / 2 };
          }
        } else if (laEff.type === 'SPELL_CAST' && laEff.player) {
          const bf = document.querySelector('.battlefield-frame');
          if (bf) {
            const br = bf.getBoundingClientRect();
            src = {
              x: br.left + br.width / 2,
              y: laEff.player === 'player' ? br.top + br.height * 0.68 : br.top + br.height * 0.32,
            };
          }
        }
        if (!src) {
          const bf = document.querySelector('.battlefield-frame');
          const br = bf ? bf.getBoundingClientRect()
            : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
          src = { x: br.left + br.width / 2, y: br.top + br.height / 2 };
        }

        effectDamages.forEach((d, i) => {
          setTimeout(() => {
            playEffectBolt({
              from: src,
              toEl: d.el,
              color,
              onImpact: () => playEffectHit(d.el, d.amount, color),
            });
          }, effectDelay + i * 200);
        });
      }

      // R34ee : flash de stats (un peu décalé pour ne pas masquer l'impact)
      if (statChanges.length > 0) {
        setTimeout(() => {
          statChanges.forEach((sc) => playStatFlash(sc.el, sc));
        }, effectDelay + (effectDamages.length > 0 ? 500 : 0));
      }
    }

    // ── 4. PV touchés (hors résolution déjà gérée) ─────────────
    if (!resolutionHandled) {
      const hitLife = (sel, delta) => {
        const el = document.querySelector(sel);
        if (!el || !(delta > 0)) return;
        const doHit = () => playLifeHit(el, delta);
        if (effectDelay > 0) {
          const bf = document.querySelector('.battlefield-frame');
          const br = bf ? bf.getBoundingClientRect() : { left: window.innerWidth / 2, top: window.innerHeight / 2, width: 0, height: 0 };
          setTimeout(() => {
            playEffectBolt({
              from: { x: br.left + br.width / 2, y: br.top + br.height * 0.42 },
              toEl: el,
              color: effectColor,
              onImpact: doHit,
            });
          }, effectDelay);
        } else {
          doHit();
        }
      };
      hitLife('.player-life', lifeDelta.player);
      hitLife('.opponent-life', lifeDelta.opponent);
      // R34ee : gains de PV (soins, effets) — anneau vert + nombre
      if (lifeDelta.player < 0) {
        playLifeGain(document.querySelector('.player-life'), -lifeDelta.player);
      }
      if (lifeDelta.opponent < 0) {
        playLifeGain(document.querySelector('.opponent-life'), -lifeDelta.opponent);
      }
    }

    // ── 5. Sorts et pièges ─────────────────────────────────────
    // (R34hh : si le showcase a déjà été joué pour ce sort, on saute
    // l'éclat court pour ne pas doubler l'annonce.)
    if (isNewAction && la.type === 'SPELL_CAST' && la.player && !effectDelay) {
      playSpellCast({
        card: la.card || { element: 'neutral', name: la.message },
        caster: la.player,
        getCardElement,
      });
    }
    if (isNewAction && la.type === 'TRAP_SET' && la.player) {
      playTrapSet({ caster: la.player });
    }

    // Snapshot pour le prochain cycle (APRÈS traitement — les morts
    // utilisent les positions du cycle précédent)
    prevRef.current = snapshotState(gameState);
    refreshRects();
  }, [gameState, getCardElement]);
}
